// EL ENVOLTORIO REACT DEL MÓDULO DE NAVEGACIÓN · TC-DR-AT.
//
// ENVOLTORIO, NO CIRUGÍA. Los hooks de aquí son OBSERVADORES: se añaden en
// una línea junto al `useState` que la pantalla ya tiene, sin cambiarlo.
//
//     const [cajon, setCajon] = useState(false);
//     useCapa(cajon, () => setCajon(false));          // ← lo único nuevo
//
// La pantalla sigue abriendo y cerrando como siempre; el hook mira ese
// estado y lleva la cuenta en el historial. Cuando el usuario pulsa atrás,
// el módulo llama a la función de cerrar que se le dio. Así la flecha «←»
// de la pantalla y el botón del teléfono recorren la misma pila.
//
// Los callbacks cambian de identidad en cada render: se guardan en una
// referencia que se actualiza EN UN EFECTO (el linter de la casa prohíbe
// escribir una referencia durante el render).
import { useCallback, useEffect, useId, useMemo, useRef, useSyncExternalStore } from 'react';
import { navegacion } from './navegacion';

function escribible(v) {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** Lo que la aplicación necesita saber de la navegación, al día. */
export function useNavegacion() {
  const pantalla = useSyncExternalStore(navegacion.suscribir, navegacion.pantalla, navegacion.pantalla);
  const aviso = useSyncExternalStore(navegacion.suscribir, navegacion.avisoDeSalida, navegacion.avisoDeSalida);
  const confirmacion = useSyncExternalStore(navegacion.suscribir, navegacion.confirmacion, navegacion.confirmacion);
  return {
    pantalla, aviso, confirmacion,
    ir: navegacion.ir, atras: navegacion.atras, enRaiz: navegacion.enRaiz, responder: navegacion.responder,
  };
}

/**
 * Una CAPA: algo que tapa la pantalla (menú, hoja, modal, cámara).
 * Mientras `abierta` es verdadero ocupa una entrada del historial; atrás
 * llama a `cerrar()`; cerrarla por su «X» consume la entrada.
 */
export function useCapa(abierta, cerrar, { bloqueada = false, tieneCambios, detalle } = {}) {
  const id = useId();
  const ref = useRef({ cerrar, bloqueada, tieneCambios, detalle });
  useEffect(() => { ref.current = { cerrar, bloqueada, tieneCambios, detalle }; });
  useEffect(() => {
    if (!abierta) return undefined;
    navegacion.abrirCapa(id, {
      cerrar: () => ref.current.cerrar?.(),
      bloqueada: () => !!ref.current.bloqueada,
      tieneCambios: () => !!ref.current.tieneCambios?.(),
      detalle: ref.current.detalle,
    });
    return () => navegacion.cerrarCapa(id);
  }, [abierta, id]);
}

/**
 * Una SUBPANTALLA: una ficha, un detalle, una pestaña que no es la base.
 *
 *   useSubpantalla('sku', !!ficha, () => setFicha(null), { valor: ficha, abrir: setFicha, enUrl: true });
 *
 * Atrás llama a `cerrar()`. Si `valor` es un texto o un número, adelante y
 * la recarga la reabren con `abrir(valor)`. `reemplaza`: cambiar entre dos
 * valores abiertos sustituye la entrada en vez de apilar (pestañas).
 *
 * ABIERTA AL MONTAR sin estar en el historial (la ficha que llega por el
 * contexto del módulo): no empuja nada, porque la entrada del módulo ya la
 * representa. Al cerrarse, la entrada deja de pedirla.
 */
export function useSubpantalla(clave, abierta, cerrar,
                               { valor = true, abrir, enUrl = false, reemplaza = false } = {}) {
  const ref = useRef({ cerrar, abrir });
  useEffect(() => { ref.current = { cerrar, abrir }; });
  const sabeAbrir = !!abrir;
  const valorEscrito = escribible(valor) ? valor : true;
  const primera = useRef(true);
  const porContexto = useRef(false);
  const empujada = useRef(false);

  useEffect(() => navegacion.registrarSub(clave, {
    cerrar: () => ref.current.cerrar?.(),
    abrir: sabeAbrir ? (v) => ref.current.abrir?.(v) : undefined,
  }), [clave, sabeAbrir]);

  useEffect(() => {
    const esPrimera = primera.current;
    primera.current = false;
    if (esPrimera) {
      const enHistorial = navegacion.estadoDeEntrada()?.subs?.find((x) => x.clave === clave);
      if (abierta && !enHistorial) { porContexto.current = true; return; }
      if (!abierta && enHistorial && enHistorial.valor !== true && ref.current.abrir) {
        ref.current.abrir(enHistorial.valor);          // recarga sobre una ficha: se reabre
        return;
      }
    }
    if (abierta) {
      if (porContexto.current) return;
      // Idempotente: la misma clave con el mismo valor no hace nada; con
      // otro valor sustituye la entrada.
      navegacion.abrirSub(clave, { valor: valorEscrito, enUrl, reemplaza });
      empujada.current = true;
    } else if (empujada.current) {
      empujada.current = false;
      navegacion.cerrarSub(clave);
    } else if (porContexto.current) {
      porContexto.current = false;
      const ctx = navegacion.pantalla()?.ctx;
      if (ctx && Object.prototype.hasOwnProperty.call(ctx, clave)) {
        const resto = { ...ctx };
        delete resto[clave];
        navegacion.reescribirContexto(resto);
      }
    }
  }, [abierta, clave, valorEscrito, enUrl, reemplaza]);

  // Si la pantalla se desmonta con la ficha abierta sin haber cambiado de
  // módulo, su entrada no se queda colgando. (Tras un cambio de módulo la
  // entrada vigente ya no la nombra y esto no hace nada.)
  useEffect(() => () => {
    if (empujada.current) { empujada.current = false; navegacion.cerrarSub(clave); }
  }, [clave]);
}

/**
 * Un asistente por PASOS: subir de paso ocupa una entrada; atrás llama a
 * `fijarPaso` con el paso anterior, conservando lo capturado; bajar por
 * código (el botón «Paso anterior») consume las entradas sobrantes.
 */
export function usePaso(clave, paso, fijarPaso, { inicial = 0 } = {}) {
  const ref = useRef(fijarPaso);
  useEffect(() => { ref.current = fijarPaso; });
  useEffect(() => navegacion.registrarPasos(clave, { fijar: (n) => ref.current?.(n), inicial }),
    [clave, inicial]);
  useEffect(() => { navegacion.paso(paso, { clave }); }, [paso, clave]);
}

/**
 * TRABAJO SIN GUARDAR. La pantalla declara cuándo lo tiene; antes de
 * abandonarla, atrás pregunta «¿Salir sin guardar?». `tieneCambios` es
 * síncrona, barata y sin efectos.
 *
 * `sub`: la clave de la subpantalla donde vive el formulario (el editor
 * que se abre en línea). Con ella, atrás pregunta también al cerrar esa
 * subpantalla, y no solo al salir de la pantalla entera.
 */
export function useCambiosSinGuardar(tieneCambios, { detalle, sub } = {}) {
  const ref = useRef(tieneCambios);
  useEffect(() => { ref.current = tieneCambios; });
  useEffect(() => navegacion.registrarCambios(() => !!ref.current?.(), { detalle, sub }), [detalle, sub]);
}

/**
 * UN FORMULARIO TOCADO, sin tocar sus campos. Para los formularios que no
 * viven en un `Modal` (ésos ya los vigila `lib/dialogo.js`):
 *
 *   const [vigilar, limpiar] = useFormularioTocado({ sub: 'nueva' });
 *   <form ref={vigilar} …>
 *
 * Si alguien escribe en un campo de dentro, atrás pregunta antes de salir.
 * `limpiar()` se llama cuando lo escrito ya se guardó.
 *
 * Igual que en los diálogos, no cuentan los buscadores (`type="search"`)
 * ni lo que vaya dentro de `[data-sin-guardia]`.
 */
export function useFormularioTocado({ detalle, sub, activo = true } = {}) {
  const tocado = useRef(false);
  const nodo = useRef(null);
  const quitar = useRef(null);
  const escuchar = useRef(null);
  useEffect(() => {
    escuchar.current = (e) => {
      const campo = e.target;
      if (campo?.type === 'search' || campo?.closest?.('[data-sin-guardia]')) return;
      tocado.current = true;
    };
  });
  // Una referencia por función: React la llama con el nodo al montarlo y
  // con `null` al desmontarlo, también si el contenedor aparece después.
  const ref = useCallback((el) => {
    quitar.current?.();
    quitar.current = null;
    nodo.current = el;
    // El contenedor se fue (el formulario se cerró): ya no hay nada tocado.
    if (!el) { tocado.current = false; return; }
    const oir = (e) => escuchar.current?.(e);
    el.addEventListener('input', oir, true);
    el.addEventListener('change', oir, true);
    quitar.current = () => {
      el.removeEventListener('input', oir, true);
      el.removeEventListener('change', oir, true);
    };
  }, []);
  const limpiar = useCallback(() => { tocado.current = false; }, []);
  useCambiosSinGuardar(() => activo && tocado.current, { detalle, sub });
  return useMemo(() => [ref, limpiar], [ref, limpiar]);
}
